import { Link, useNavigate } from "react-router-dom";
import { Building2, Plus, Search } from "lucide-react";

import { adminAgenciesApi } from "@/api/admin";
import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Pagination } from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useResourceList } from "@/hooks/useResourceList";

const COLUMNS = [
  { key: "name", header: "Name", render: (row) => <span className="font-medium">{row.name}</span> },
  { key: "slug", header: "Slug", render: (row) => row.slug },
  { key: "timezone", header: "Timezone", render: (row) => row.timezone },
  { key: "contact_email", header: "Email", render: (row) => row.contact_email || "—" },
  { key: "contact_phone", header: "Phone", render: (row) => row.contact_phone || "—" },
  {
    key: "is_active",
    header: "Status",
    render: (row) => (
      <Badge variant={row.is_active ? "default" : "secondary"}>
        {row.is_active ? "Active" : "Inactive"}
      </Badge>
    ),
  },
];

export function AgenciesPage() {
  const navigate = useNavigate();
  const { rows, count, isLoading, error, page, setPage, search, setSearch } =
    useResourceList(adminAgenciesApi);

  if (isLoading && rows.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Organizations"
        description="Manage the tenant agencies on the platform."
        actions={
          <Button asChild>
            <Link to="/admin/agencies/new">
              <Plus />
              Add organization
            </Link>
          </Button>
        }
      />

      <PageBody>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search organizations"
        />

        <Alert>{error}</Alert>

        {rows.length === 0 ? (
          <EmptyState
            icon={search ? Search : Building2}
            tone={search ? "slate" : "indigo"}
            title={search ? "No matches for that search." : "No organizations yet"}
            message={
              search
                ? "Try a different name or slug."
                : "Add your first organization to get started."
            }
            action={
              search ? null : (
                <Button asChild>
                  <Link to="/admin/agencies/new">
                    <Plus />
                    Add organization
                  </Link>
                </Button>
              )
            }
          />
        ) : (
          <div aria-busy={isLoading}>
            <DataTable
              columns={COLUMNS}
              rows={rows}
              getRowKey={(row) => row.id}
              onRowClick={(row) => navigate(`/admin/agencies/${row.id}`)}
            />
          </div>
        )}

        <Pagination page={page} count={count} onChange={setPage} />
      </PageBody>
    </PageShell>
  );
}
